import { useState, useMemo } from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Edit,
  Delete,
  History,
  AddBox,
} from "@mui/icons-material";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import SortableColumnHeader from "./SortableColumnHeader";

const RowWithPreferences = ({
  donor,
  allDonations,
  payPurposes,
  onEdit,
  onDelete,
  onView,
  onAppend,
  role,
  columnPreferences,
  parentColumnPreferences,
  reorderColumns,
  reorderParentColumns,
  toggleParentColumnVisibility,
  resetParentToDefault,
}) => {
  const [open, setOpen] = useState(false);
  const visibleColumns = columnPreferences.filter((c) => c.visible !== false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const donorDonations = useMemo(() => {
    if (!Array.isArray(allDonations)) return [];
    return allDonations
      .filter((d) => d.donorId === donor.donorId)
      .map((donation) => {
        const payPurposeData =
          payPurposes.find(
            (p) =>
              p.donorId === donation.donorId &&
              p.transactionId === donation.transactionId
          ) || {};

        return {
          ...donation,
          tributeName: payPurposeData.tributeName || "",
          sharma: payPurposeData.sharma || "",
          poojaDate: payPurposeData.poojaDate || "",
          englishMonths: payPurposeData.englishMonths || "",
          tamilMonths: payPurposeData.tamilMonths || "",
          nachathiram: payPurposeData.nachathiram || "",
          thithi: payPurposeData.thithi || "",
          utsavam: payPurposeData.utsavam || "",
        };
      })
      .sort((a, b) => {
        if (a.bookNo < b.bookNo) return -1;
        if (a.bookNo > b.bookNo) return 1;
        if (a.receiptNo < b.receiptNo) return -1;
        if (a.receiptNo > b.receiptNo) return 1;
        return 0;
      });
  }, [donor.donorId, allDonations, payPurposes]);

  const renderActionButtons = (donation) => (
    <Box display="flex">
      {(role === "admin1" ||
        role === "admin2" ||
        role === "editor2" ||
        role === "superadmin") && (
        <IconButton
          onClick={() => onEdit(donation)}
          title="Edit"
          style={{ color: "#1976d2" }}
        >
          <Edit />
        </IconButton>
      )}
      {(role === "superadmin" || role === "admin1" || role === "admin2") && (
        <IconButton
          onClick={() => onDelete(donation)}
          title="Delete"
          style={{ color: "#d32f2f" }}
        >
          <Delete />
        </IconButton>
      )}
      {(role === "admin1" || role === "admin2" || role === "superadmin") && (
        <IconButton
          onClick={() => onView(donation)}
          title="View History"
          style={{ color: "#1976D2" }}
        >
          <History />
        </IconButton>
      )}
    </Box>
  );

    const renderCellContent = (column, donation) => {
    if (column.id === "actions") {
      return renderActionButtons(donation);
    }

    if (column.id === "receiptDate" || column.id === "instrumentDate") {
      const date = donation[column.field];
      return date ? new Date(date).toLocaleDateString() : "-";
    }

    return donation[column.field] || "-";
  };

  
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columnPreferences.findIndex((c) => c.id === active.id);
    const newIndex = columnPreferences.findIndex((c) => c.id === over.id);
    reorderColumns(oldIndex, newIndex);
  };

  const renderParentRowCell = (col) => {
    if (col.id === "expand") {
      return (
        <TableCell key={col.id} onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      );
    }

    if (col.id === "actions") {
      return (
        <TableCell key={col.id}>
          {(role === "admin1" ||
            role === "admin2" ||
            role === "superadmin" ||
            role === "editor2") && (
            <IconButton
              onClick={() => onAppend(donor)}
              title="Add Donation"
              style={{ color: "#1976d2" }}
            >
              <AddBox />
            </IconButton>
          )}
        </TableCell>
      );
    }

    return (
      <TableCell
        key={col.id}
        onClick={() => setOpen(!open)}
        style={{ cursor: "pointer" }}
      >
        {col.field ? donor[col.field] || "-" : "-"}
      </TableCell>
    );
  };

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: "#f9f9f9",
          "&:hover": { backgroundColor: "#f1f1f1" },
        }}
      >
        {parentColumnPreferences && parentColumnPreferences.map((col) => renderParentRowCell(col))}
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={parentColumnPreferences ? parentColumnPreferences.length : 7}
          sx={{ p: 0, border: 0, backgroundColor: "#fafafa" }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1, borderTop: "2px solid #ddd", pt: 1, pb: 1 }}>
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  maxWidth: "100%",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <DndContext sensors={sensors} onDragEnd={onDragEnd}>
                  <SortableContext
                    items={visibleColumns.map((c) => c.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Table
                      size="small"
                      sx={{ minWidth: 1600, width: "max-content" }}
                    >
                      <TableHead>
                        <TableRow>
                          {visibleColumns.map((col) => (
                            <SortableColumnHeader key={col.id} column={col}>
                              <TableCell sx={{ width: col.width }}>
                                {col.label}
                              </TableCell>
                            </SortableColumnHeader>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {donorDonations.map((donation) => (
                          <TableRow key={donation._id}>
                            {visibleColumns.map((col) => (
                              <TableCell key={col.id}>
                                {renderCellContent(col, donation)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </SortableContext>
                </DndContext>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default RowWithPreferences;
